// components/Bill.js

import React, { useState, useEffect, useRef } from 'react';
import Table from "react-bootstrap/Table";
import Card from "react-bootstrap/Card";
import Nav from "react-bootstrap/Nav";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileLines, faStickyNote } from '@fortawesome/free-solid-svg-icons';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import DatePicker from 'react-datepicker';
import { Container, Row, Col, Modal, Button, Form } from 'react-bootstrap';
import PaidBillDropdownButton from "./PaidBillDropdownButton";
import PaidPayDropdownButton from "./PaidPayDropdownButton";
import UpdatePayModal from './UpdatePayModal';
import UpdateBillModal from './UpdateBillModal';
import PhoneBillDetailsModal from './mobile/PhoneBillDetailsModal';
import UpdateNotesModal from './UpdateBillNoteModal';
import { getBills, getTotalThisWeek, getTotalNextWeek, getTotalFortnight } from '../../services/BillService';
import { updateBillPaidStatus, updateBillDetails, deleteBill } from '../../services/BillService';
import { formatReadableDate } from '../../utils/dateUtils';
import { getPays, filterPaysByCurrentWeek, filterPaysByNextWeek, updatePayStatus, updatePayBankAccountTransferStatus } from '../../services/PayService';
import 'react-datepicker/dist/react-datepicker.css';

// Debug utility functions - only log in development environment
const debugLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

const debugError = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(...args);
  }
};

const debugWarn = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(...args);
  }
};

const Bill = ({
  bill,
  pay,
  onUpdatePaidDate,
  bankAccountId,
  isSelected,
  onSelect,
  onDoubleClick,
  onDelete,
  showAllBills = false,
  bankAccounts = [],
  layout,
}) => {
  const isPay = bill.hasOwnProperty('description') && bill.description === "incoming pay";
  const [isPaid, setIsPaid] = useState(bill.paid);
  const [paidDueDate, setPaidDueDate] = useState(bill.paid_date);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDatePopup, setSelectedDatePopup] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const longPressTimer = useRef(null);
  const [localBill, setLocalBill] = useState(bill);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSeries, setDeleteSeries] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);
  const [editedBill, setEditedBill] = useState({
    name: bill.name,
    amount: bill.amount,
    due_date: bill.due_date,
    paid_date: bill.paid_date,
    bill_series_id: bill.bill_series_id,
    bank_account: bill.bank_account,
    frequency: bill.frequency || 'by_date',
    description: bill.description || null,
    notes: bill.notes || null,
    due_day: bill.due_day || null,
    num_bills: bill.num_bills || 1
  });

  // Mobile detection state
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);

  // Check if device is mobile portrait
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Phone detection: either portrait (width <= 480) or landscape (height <= 480 && width <= 896)
      const isPhonePortrait = width <= 480;
      const isPhoneLandscape = height <= 480 && width <= 896;
      setIsMobilePortrait(isPhonePortrait || isPhoneLandscape);
    };
    
    checkDevice();
    window.addEventListener('resize', checkDevice);
    
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const formattedDueDate = formatReadableDate(bill.due_date);
  const formattedPaidDate = formatReadableDate(bill.paid_date);

  useEffect(() => {
    if (isPay) {
      setIsPaid(bill.transferMade);
    } else {
      setIsPaid(bill.paid);
    }
  }, [bill]);

  // Add an effect to double-check the paid status when bill prop changes
  useEffect(() => {
    if (isPay) {
      // Make sure our local state always matches the transferMade status
      if (bill.transferMade !== isPaid) {
        setIsPaid(bill.transferMade);
      }
    } else {
      // Make sure our local state always matches the paid status for regular bills
      if (bill.paid !== isPaid) {
        setIsPaid(bill.paid);
      }
    }
  }, [bill, isPay, isPaid]);

  const handleHideDatePickerPopup = () => {
    setShowDatePicker(false);
    setSelectedDatePopup(selectedDate);
  };

  const handleConfirmDatePicker = () => {
    setShowDatePicker(false);
    setSelectedDate(selectedDatePopup);
    setIsPaid(true);

    setEditedBill(prevState => ({ ...prevState, paid_date: selectedDatePopup }));

    const updatedBill = {
      ...bill,
      paid: true,
      paid_date: selectedDatePopup,
      frequency: bill.frequency || 'by_date',
      description: bill.description || null,
      notes: bill.notes || null,
      due_day: bill.due_day || null,
      num_bills: bill.num_bills || 1
    };
    updateBillPaidStatus(bill.id, true, selectedDatePopup, updatedBill)
      .then((updatedBill) => {
        onUpdatePaidDate(updatedBill);
      })
      .catch((error) => {
        debugError('Error updating bill status:', error.message);
        debugError('Error updating bill status (full details):', error);
      });
  };

  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  useEffect(() => {
    setPaidDueDate(new Date(bill.due_date));
  }, [bill.due_date]);

  const handleBillDropdownButtonClick = (billId, title) => {
    if (title === 'PaidDue') {
      setIsPaid(true);
      setSelectedDate(paidDueDate);  // Set to yesterday's date
      const updatedBill = {
        ...bill,
        paid: true,
        paid_date: paidDueDate,
        frequency: bill.frequency || 'by_date',
        description: bill.description || null,
        notes: bill.notes || null,
        due_day: bill.due_day || null,
        num_bills: bill.num_bills || 1
      };
      setEditedBill(prevState => ({ ...prevState, paid_date: paidDueDate }));
      updateBillPaidStatus(bill.id, true, paidDueDate, updatedBill)
        .then((updatedBill) => {
          onUpdatePaidDate(updatedBill);
        })
        .catch((error) => {
          debugError('Error updating bill status:', error.message);
          debugError('Error updating bill status (full details):', error);
        });
    } else if (title === 'Today') {
      setIsPaid(true);
      setSelectedDate(today);
      const updatedBill = {
        ...bill,
        paid: true,
        paid_date: today,
        frequency: bill.frequency || 'by_date',
        description: bill.description || null,
        notes: bill.notes || null,
        due_day: bill.due_day || null,
        num_bills: bill.num_bills || 1
      };
      setEditedBill(prevState => ({ ...prevState, paid_date: today }));
      updateBillPaidStatus(bill.id, true, today, updatedBill)
        .then((updatedBill) => {
          onUpdatePaidDate(updatedBill);
        })
        .catch((error) => {
          debugError('Error updating bill status:', error.message);
          debugError('Error updating bill status (full details):', error);
        });
    } else if (title === 'Yesterday') {
      setIsPaid(true);
      setSelectedDate(yesterday);  // Set to yesterday's date
      const updatedBill = {
        ...bill,
        paid: true,
        paid_date: yesterday,
        frequency: bill.frequency || 'by_date',
        description: bill.description || null,
        notes: bill.notes || null,
        due_day: bill.due_day || null,
        num_bills: bill.num_bills || 1
      };
      setEditedBill(prevState => ({ ...prevState, paid_date: yesterday }));
      updateBillPaidStatus(bill.id, true, yesterday, updatedBill)
        .then((updatedBill) => {
          onUpdatePaidDate(updatedBill);
        })
        .catch((error) => {
          debugError('Error updating bill status:', error.message);
          debugError('Error updating bill status (full details):', error);
        });
    } else if (title === 'PaidOn') {
      setShowDatePicker(true);
      setSelectedDatePopup(selectedDate);
    } else if (title === 'Unpaid') {
      setIsPaid(false);
      setSelectedDate(null);
      const updatedBill = {
        ...bill,
        paid: false,
        paid_date: null,
        frequency: bill.frequency || 'by_date',
        description: bill.description || null,
        notes: bill.notes || null,
        due_day: bill.due_day || null,
        num_bills: bill.num_bills || 1
      };
      setEditedBill(prevState => ({ ...prevState, paid_date: null }));
      updateBillPaidStatus(bill.id, false, null, updatedBill)
        .then((updatedBill) => {
          onUpdatePaidDate(updatedBill);
        })
        .catch((error) => {
          debugError('Error updating bill status:', error.message);
          debugError('Error updating bill status (full details):', error);
        });
    } else if (title === 'Update') {
      setShowUpdateModal(true);
    } else if (title === 'Delete') {
      setShowDeleteModal(true);
    }
  };

  const handleSaveChanges = async () => {
    try {
      // Check if trying to apply to all but bill_series_id is missing
      if (applyToAll && (!bill.bill_series_id || typeof bill.bill_series_id !== 'string' || bill.bill_series_id.trim() === '')) {
        // This bill isn't part of a series, show a message and don't apply to all
        setApplyToAll(false);
        debugWarn("Cannot apply to all bills in series - this bill is not part of a series");
        alert("Changes cannot be applied to following bills.");
        return;
      }

      // Make sure all required fields are included
      const completeEditedBill = {
        ...bill,          // Include all original bill data first
        ...editedBill,    // Then override with edited fields
        // Ensure required fields are present and properly formatted
        name: editedBill.name || bill.name,
        amount: editedBill.amount || bill.amount,
        due_date: editedBill.due_date || bill.due_date,
        bank_account: editedBill.bank_account || bill.bank_account,
        frequency: bill.frequency || 'by_date',
        due_day: bill.due_day,
        num_bills: bill.num_bills || 1,
        notes: bill.notes || null,
        description: bill.description || null
      };
      
      const updatedBill = await updateBillDetails(
        bill.id, 
        completeEditedBill, 
        applyToAll ? bill.bill_series_id : null
      );
      onUpdatePaidDate(updatedBill);
      setShowUpdateModal(false);
    } catch (error) {
      debugError('Error updating bill details:', error.message);
      debugError('Error updating bill details (full details):', error);
      alert('Failed to update bill. Please try again.');
    }
  };

  const handlePayDropdownButtonClick = (title) => {
    if (title === 'Paid') {
      updatePayStatus(bill.id, true);
    } else if (title === 'Unpaid') {
      updatePayStatus(bill.id, false);
    }
  };

  const handleUpdateNotes = (updatedBill) => {
    // Update the local bill state
    setLocalBill(updatedBill);

    // Call the parent update method
    onUpdatePaidDate(updatedBill);
  };

  const updatePayStatus = (payId, isPaid) => {
    // Update local state immediately to show feedback
    setIsPaid(isPaid);
    
    // Create a full copy of the original bill with all needed properties
    const updatedBill = {
      ...bill,
      paid: isPaid,  // Update paid status 
      transferMade: isPaid  // Update transferMade status
    };
    
    // Call the API to update transfer status
    updatePayBankAccountTransferStatus(bill.id, bankAccountId, isPaid)
      .then((response) => {
        // API call succeeded - the parent component will update the global state
      })
      .catch((error) => {
        // Revert the local state if there's an error
        debugError('Error updating payBankAccount:', error.message);
        debugError('Error updating payBankAccount (full details):', error);
        setIsPaid(!isPaid); // Revert to previous state
        alert('Please try again.');
        return; // Exit early, don't update parent state
      });
    
    // Pass the updated bill to the parent component immediately
    // This ensures the UI updates without waiting for the API response
    onUpdatePaidDate(updatedBill);
  };

  const handleDelete = async (deleteSeries) => {
    try {
      await deleteBill(bill.id, deleteSeries);
      setShowDeleteModal(false);
      onDelete(bill.id, deleteSeries ? bill.bill_series_id : null);
    } catch (error) {
      debugError('Error deleting bill:', error.message);
      debugError('Error deleting bill (full details):', error);
    }
  };

  const now = new Date();
  const dueDate = new Date(bill.due_date);
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const dayAfterTomorrow = new Date(now);
  dayAfterTomorrow.setDate(now.getDate() + 2);
  const isOverdue = dueDate < now;

  const isDueSoon = dueDate.toDateString() === now.toDateString() ||
                    dueDate.toDateString() === tomorrow.toDateString() ||
                    dueDate.toDateString() === dayAfterTomorrow.toDateString();

  const rowStyle = {
    backgroundColor: isPaid ? '#e0e0e0' : (isPay ? '#d0e0e3' : 'inherit'),
    color: isPaid ? '#a0a0a0' : 'inherit',
    cursor: 'pointer',
  };

  const textStyle = {
    color: isPay ? 'inherit' : (
              isOverdue && !isPaid ? '#d9534f' : // Muted red if overdue and unpaid
              isDueSoon && !isPaid ? '#f5c542' : // Yellow if due soon and unpaid
              'inherit'  // Default color
            ),
  };

  const buttonStyle = {
    backgroundColor: isOverdue && !isPaid ? '#d9534f' : 'inherit',  // Red if overdue and unpaid
    color: isOverdue && !isPaid ? '#ffffff' : 'inherit',  // White text if button is red
  };

  const renderPaidDateAndDeleteModals = () => (
    <>
      <Modal show={showDatePicker} onHide={handleHideDatePickerPopup} dialogClassName="mobile-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Select Paid Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DatePicker
            selected={selectedDatePopup}
            onChange={(date) => setSelectedDatePopup(date)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHideDatePickerPopup}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmDatePicker}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        dialogClassName="mobile-responsive-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete "{bill.name}"?</p>
          {bill.bill_series_id && (
            <Form.Check
              type="checkbox"
              label="Delete all future bills in this series"
              onChange={(e) => setDeleteSeries(e.target.checked)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => handleDelete(deleteSeries)}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );

  const rowProps = {
//    ...rowStyle,
    className: `bill-row ${isSelected ? 'selected' : ''}`,
    onClick: (e) => onSelect(bill, e),
//    onDoubleClick: () => onDoubleClick(bill),
    style: rowStyle
  };

  // Mobile card layout (Phone Bills UX)
  if (layout === 'card') {
    const clearLongPress = () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
        longPressTimer.current = null;
      }
    };

    const handleLongPressStart = () => {
      clearLongPress();
      longPressTimer.current = setTimeout(() => {
        setShowDetailsModal(true);
        longPressTimer.current = null;
      }, 500);
    };

    const cardClass = [
      'phone-bill-card',
      isPaid ? 'phone-bill-card--paid' : '',
      isOverdue && !isPaid ? 'phone-bill-card--overdue' : '',
      isDueSoon && !isPaid && !isOverdue ? 'phone-bill-card--due-soon' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <>
        <div
          className={cardClass}
          onContextMenu={(e) => {
            e.preventDefault();
            setShowDetailsModal(true);
          }}
        >
          <div
            className="phone-bill-card-main"
            onTouchStart={handleLongPressStart}
            onTouchEnd={clearLongPress}
            onTouchMove={clearLongPress}
            onTouchCancel={clearLongPress}
          >
            <div className="phone-bill-card-name" style={textStyle}>
              {bill.name}
            </div>
            <div className="phone-bill-card-meta">
              <span className="phone-bill-card-amount" style={textStyle}>
                ${bill.amount}
              </span>
              <span style={textStyle}>Due {formattedDueDate}</span>
              {bill.paid_date && (
                <span style={textStyle}>Paid {formattedPaidDate}</span>
              )}
            </div>
            {(showAllBills ||
              bankAccounts?.find((account) => account.id === bill.bank_account)?.nickname) && (
              <div className="phone-bill-card-account">
                {bankAccounts?.find((account) => account.id === bill.bank_account)?.nickname ||
                  'Unknown Account'}
              </div>
            )}
          </div>
          <div className="phone-bill-card-action" onClick={(e) => e.stopPropagation()}>
            {!isPay && (
              <PaidBillDropdownButton
                onClick={handleBillDropdownButtonClick}
                isPaid={isPaid}
                isOverdue={isOverdue}
              />
            )}
            {isPay && (
              <PaidPayDropdownButton
                onClick={handlePayDropdownButtonClick}
                isPaid={isPaid}
                onStatusChange={setIsPaid}
              />
            )}
          </div>
        </div>
        <PhoneBillDetailsModal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          bill={bill}
        />
        <UpdateNotesModal
          show={showNotesModal}
          handleClose={() => setShowNotesModal(false)}
          bill={bill}
          onUpdateNotes={handleUpdateNotes}
        />
        <UpdateBillModal
          show={showUpdateModal}
          handleClose={() => setShowUpdateModal(false)}
          editedBill={editedBill}
          setEditedBill={setEditedBill}
          applyToAll={applyToAll}
          setApplyToAll={setApplyToAll}
          handleSaveChanges={handleSaveChanges}
        />
        {renderPaidDateAndDeleteModals()}
      </>
    );
  }

  // Mobile Portrait Layout (legacy table row — desktop path uses phone layout instead)
  if (isMobilePortrait && layout !== 'desktop') {
    return (
      <tr key={bill.id} className="mobile-portrait-bill-row" style={rowStyle} onClick={(e) => onSelect(bill, e)} {...rowProps}>
        <td className="mobile-portrait-main-info">
          <div className="mobile-bill-name-row">
            {bill.description && (
              <FontAwesomeIcon
                icon={faFileLines}
                style={{
                  color: 'rgba(108, 117, 125, 0.6)',
                  cursor: 'help',
                  fontSize: '12px',
                  marginRight: '4px'
                }}
                title={bill.description}
              />
            )}
            <span className="mobile-bill-name" style={{ fontWeight: 'bold', ...textStyle }}>{bill.name}</span>
            {bill.notes && (
              <FontAwesomeIcon
                icon={faStickyNote}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNotesModal(true);
                }}
                style={{
                  color: 'rgba(108, 117, 125, 0.6)',
                  cursor: 'pointer',
                  fontSize: '12px',
                  marginLeft: '4px'
                }}
              />
            )}
          </div>
          <div className="mobile-bill-details" style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
            <span style={{ fontWeight: '500', ...textStyle }}>${bill.amount}</span>
            <span style={{ margin: '0 6px', ...textStyle }}>•</span>
            <span style={{ fontWeight: 'bold', ...textStyle }}>{formattedDueDate}</span>
          </div>
          {(showAllBills || bankAccounts?.find(account => account.id === bill.bank_account)?.nickname) && (
            <div className="mobile-bank-account" style={{ fontSize: '10px', fontStyle: 'italic', color: '#9ca3af', marginTop: '1px' }}>
              {bankAccounts?.find(account => account.id === bill.bank_account)?.nickname || 'Unknown Account'}
            </div>
          )}
        </td>
        <td className="mobile-portrait-action" style={{ width: '80px', textAlign: 'center', verticalAlign: 'middle' }}>
          {!isPay && (
            <PaidBillDropdownButton
              onClick={handleBillDropdownButtonClick}
              isPaid={isPaid}
              isOverdue={isOverdue}
            />
          )}
          {isPay && (
            <PaidPayDropdownButton 
              onClick={handlePayDropdownButtonClick} 
              isPaid={isPaid} 
              onStatusChange={setIsPaid} 
            />
          )}
        </td>
        
        {/* Hidden modals for mobile */}
        <UpdateNotesModal
          show={showNotesModal}
          handleClose={() => setShowNotesModal(false)}
          bill={bill}
          onUpdateNotes={handleUpdateNotes}
        />
        <UpdateBillModal
          show={showUpdateModal}
          handleClose={() => setShowUpdateModal(false)}
          editedBill={editedBill}
          setEditedBill={setEditedBill}
          applyToAll={applyToAll}
          setApplyToAll={setApplyToAll}
          handleSaveChanges={handleSaveChanges}
        />
        {renderPaidDateAndDeleteModals()}
      </tr>
    );
  }

  // Desktop/Landscape Layout
  return (
    <tr key={bill.id} style={rowStyle} onClick={(e) => onSelect(bill, e)} {...rowProps}>
      <td style={{ textAlign: 'left', ...textStyle }}>
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            {bill.description && (
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{bill.description}</div>
                  </Tooltip>
                }
              >
                <FontAwesomeIcon
                  icon={faFileLines}
                  style={{
                    position: 'absolute',
                    left: '2px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'rgba(108, 117, 125, 0.6)',
                    cursor: 'help',
                    fontSize: '14px',
                    marginRight: '8px'
                  }}
                />
              </OverlayTrigger>
            )}
            <h5 style={{margin: 20 }}>{bill.name}</h5>
          </div>
      </td>
      <td style={textStyle}>${bill.amount}</td>
      <td style={{ ...textStyle, fontWeight: 'bold' }}>{formattedDueDate}</td>
      <td>
        {bill.paid_date ? formattedPaidDate : (selectedDate ? selectedDate.toLocaleDateString() : '-')}
      </td>
      {showAllBills && (
        <td style={textStyle}>
          {bankAccounts.find(account => account.id === bill.bank_account)?.nickname || 'Unknown Account'}
        </td>
      )}
      {!isPay && (
        <td>
    {/* <div style={{ position: 'relative', display: 'flex', justifyContent: 'center' }}> */}
            <PaidBillDropdownButton
              onClick={handleBillDropdownButtonClick}
              isPaid={isPaid}
              isOverdue={isOverdue}  // Pass isOverdue to the button
            />
        </td>
      )}

      {isPay && (
        <td>
            <PaidPayDropdownButton onClick={handlePayDropdownButtonClick} isPaid={isPaid} onStatusChange={setIsPaid} />
        </td>
      )}

      <td style={{ width: '40px', textAlign: 'center' }}>
        {!isPay && !bill.notes && ( // Add !isPay condition
          <>
            <FontAwesomeIcon
              data-icon="add-note"
              icon={faPlus}
              onClick={(e) => {
                e.stopPropagation();
                setShowNotesModal(true); // Open modal to add note
              }}
              style={{
                color: 'rgba(108, 117, 125, 0.6)',
                cursor: 'pointer',
                fontSize: '10px',
                fontWeight: 100
              }}
            />
            <UpdateNotesModal
              show={showNotesModal}
              handleClose={() => setShowNotesModal(false)}
              bill={bill}
              onUpdateNotes={handleUpdateNotes}
            />
          </>
        )}

        {bill.notes && (
          <>
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id={`note-tooltip-${bill.id}`}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {bill.notes.length > 100 ? `${bill.notes.substring(0, 100)}... (Click to view more)` : bill.notes}
                  </div>
                </Tooltip>
              }
              trigger={['hover', 'focus']}
              rootClose
            >
              <span>
                <FontAwesomeIcon
                  data-icon="sticky-note"
                  icon={faStickyNote}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNotesModal(true);
                  }}
                  style={{
                    color: 'rgba(108, 117, 125, 0.6)',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                />
              </span>
            </OverlayTrigger>
            <UpdateNotesModal
              show={showNotesModal}
              handleClose={() => setShowNotesModal(false)}
              bill={bill}
              onUpdateNotes={handleUpdateNotes}
            />
          </>
        )}
      </td>


      {renderPaidDateAndDeleteModals()}

      <UpdateBillModal
        show={showUpdateModal}
        handleClose={() => setShowUpdateModal(false)}
        editedBill={editedBill}
        setEditedBill={setEditedBill}
        applyToAll={applyToAll}
        setApplyToAll={setApplyToAll}
        handleSaveChanges={handleSaveChanges}
      />

    </tr>
  );
};

export default Bill;
